import { nextPlannedDay, parseSchedule } from './repeatSchedule';
/**
 * Date logic for the calendar's day / week / month navigation (all local time, never mutates
 * its inputs). Weeks start on Monday, matching the ISO week numbers shown in the header.
 */

export type CalendarView = 'day' | 'week' | 'month';

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Monday of the week containing d (local time). */
export function mondayOf(d: Date): Date {
  const day = d.getDay(); // 0 = Sunday
  const diff = day === 0 ? -6 : 1 - day;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + diff);
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

/**
 * Move one period back (delta < 0) or forward (delta > 0). Months keep the day of the month
 * where possible and clamp otherwise (31 Jan + 1 month → 28/29 Feb), so repeated clicks never
 * skip a month.
 */
export function shiftPeriod(d: Date, view: CalendarView, delta: number): Date {
  if (view === 'day') return new Date(d.getFullYear(), d.getMonth(), d.getDate() + delta);
  if (view === 'week') return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7 * delta);
  const target = new Date(d.getFullYear(), d.getMonth() + delta, 1);
  const day = Math.min(d.getDate(), daysInMonth(target.getFullYear(), target.getMonth()));
  return new Date(target.getFullYear(), target.getMonth(), day);
}

/** Inclusive local-time range covered by the view around d. */
export function rangeFor(view: CalendarView, d: Date): { from: Date; to: Date } {
  if (view === 'day') {
    const from = startOfDay(d);
    return { from, to: new Date(from.getFullYear(), from.getMonth(), from.getDate(), 23, 59, 59, 999) };
  }
  if (view === 'week') {
    const from = mondayOf(d);
    return { from, to: new Date(from.getFullYear(), from.getMonth(), from.getDate() + 6, 23, 59, 59, 999) };
  }
  const from = new Date(d.getFullYear(), d.getMonth(), 1);
  return { from, to: new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999) };
}

/** The days listed by the view (day: just that day; week: Mon–Sun; month: every day). */
export function daysFor(view: CalendarView, d: Date): Date[] {
  const { from, to } = rangeFor(view, d);
  const out: Date[] = [];
  for (let x = from; x <= to; x = new Date(x.getFullYear(), x.getMonth(), x.getDate() + 1)) out.push(x);
  return out;
}

/** True if the period shown around d includes today. */
export function periodContains(view: CalendarView, d: Date, today: Date): boolean {
  const { from, to } = rangeFor(view, d);
  return today >= from && today <= to;
}

function toLocalInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Default start/end (datetime-local strings) for a new event created while looking at `selected`:
 * today → the next full hour; any other day → 09:00. Always one hour long.
 */
export function defaultNewEventTimes(selected: Date, now: Date): { start: string; end: string } {
  const start = isSameDay(selected, now)
    ? new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() + 1, 0)
    : new Date(selected.getFullYear(), selected.getMonth(), selected.getDate(), 9, 0);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return { start: toLocalInput(start), end: toLocalInput(end) };
}

/**
 * The event form uses <input type="datetime-local">, which has no time zone. Before 2026-10-09
 * that string was sent as-is and stored as UTC, so events showed up 1–2 hours late in Denmark.
 * Convert explicitly in both directions.
 */
export function localInputToIso(value: string): string {
  if (!value) return value;
  const d = new Date(value); // "YYYY-MM-DDTHH:mm" parses as LOCAL time
  return Number.isNaN(d.getTime()) ? value : d.toISOString();
}

export function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : toLocalInput(d);
}

/** Local "YYYY-MM-DD" key for grouping items by day. */
export function dayKey(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** How a day section is titled: "Today, …", "Tomorrow, …" or just the date. */
export function dayRelation(d: Date, now: Date): 'today' | 'tomorrow' | 'other' {
  if (isSameDay(d, now)) return 'today';
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  return isSameDay(d, tomorrow) ? 'tomorrow' : 'other';
}

/**
 * Tasks shown in the calendar: not finished, with a due date, grouped by the local day they are
 * due. `onlyFor` limits them to one member's tasks (a child sees their own; a grown-up's member
 * filter); otherwise every assigned or open task is included.
 */
type CalendarTask = { due_date?: unknown; is_completed?: boolean; assigned_to?: string | null; available_from?: unknown };

/** The task's due date if it belongs in the calendar (open, dated, visible, for this member). */
function calendarDue(t: CalendarTask, onlyFor: string | null | undefined, now: number): Date | null {
  if (t.is_completed || !t.due_date) return null;
  // Repeat copies waiting for their day aren't shown yet (utils/taskAvailability).
  if (t.available_from && new Date(t.available_from as any).getTime() > now) return null;
  if (onlyFor && t.assigned_to !== onlyFor) return null;
  const d = new Date(t.due_date as any);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function tasksByDueDay<T extends CalendarTask>(tasks: T[], onlyFor?: string | null, now: Date = new Date()): Record<string, T[]> {
  const out: Record<string, T[]> = {};
  for (const t of tasks) {
    const d = calendarDue(t, onlyFor, now.getTime());
    if (d) (out[dayKey(d)] ||= []).push(t);
  }
  return out;
}

/** Unfinished tasks whose due day has passed — shown at the top of today, oldest first (2026-10-12). */
export function overdueTasks<T extends CalendarTask>(tasks: T[], onlyFor?: string | null, now: Date = new Date()): T[] {
  const today = startOfDay(now).getTime();
  return tasks
    .map((t) => ({ t, d: calendarDue(t, onlyFor, now.getTime()) }))
    .filter((x): x is { t: T; d: Date } => !!x.d && x.d.getTime() < today)
    .sort((a, b) => a.d.getTime() - b.d.getTime())
    .map((x) => x.t);
}

export type TaskDueKind = 'overdue' | 'today' | 'tomorrow' | 'later';

/**
 * The note on a task in the calendar (2026-10-12): overdue / due today / due tomorrow / later,
 * plus the time when the due date has one (not midnight or 23:59, which mean "some time that day").
 */
export function taskDueInfo(due: unknown, now: Date = new Date()): { kind: TaskDueKind; time: string | null } | null {
  if (!due) return null;
  const d = new Date(due as any);
  if (Number.isNaN(d.getTime())) return null;
  const today = startOfDay(now).getTime();
  const day = startOfDay(d).getTime();
  const kind: TaskDueKind = day < today ? 'overdue' : day === today ? 'today'
    : dayRelation(d, now) === 'tomorrow' ? 'tomorrow' : 'later';
  const h = d.getHours();
  const m = d.getMinutes();
  const meaningful = !((h === 0 && m === 0) || (h === 23 && m === 59));
  const time = meaningful ? `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}` : null;
  return { kind, time };
}

// ── Planned copies of tasks on a fixed plan (2026-10-14) ─────────────────────────────────────

type PlanTask = CalendarTask & {
  id: string; title?: string; series_id?: string | null; created_at?: unknown; recreate_on_validation?: boolean | null;
  repeat_schedule?: unknown; days_to_complete?: number | null; auto_available?: boolean | null;
  points?: number; header_image?: string | null; header_image_url?: string | null;
};

/**
 * Faint "planned" entries for tasks that repeat on a fixed plan (every Thursday …), like a calendar
 * app shows the coming repeats of an event. Each is placed on its deadline day (its first day when
 * it has no deadline). Covers the copy that is waiting for its day (hidden from tasksByDueDay) and
 * the coming rounds after that, assuming each round is done on time. Not real tasks: `planned: true`.
 */
export function plannedTaskEntries<T extends PlanTask>(
  tasks: T[], onlyFor: string | null | undefined, now: Date = new Date(), days = 62,
): Record<string, Array<T & { planned: true }>> {
  const latest = new Map<string, T>();
  const time = (t: T) => (t.created_at ? new Date(t.created_at as any).getTime() : 0);
  for (const t of tasks) {
    const key = t.series_id || t.id;
    const cur = latest.get(key);
    if (!cur || time(t) > time(cur)) latest.set(key, t);
  }
  const until = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
  const out: Record<string, Array<T & { planned: true }>> = {};
  for (const t of Array.from(latest.values())) {
    const plan = parseSchedule(t.repeat_schedule);
    if (!plan || !t.recreate_on_validation) continue;
    const who = t.auto_available === false ? t.assigned_to ?? null : null;
    if (onlyFor && who !== onlyFor) continue;
    const waiting = !t.is_completed && t.available_from && new Date(t.available_from as any).getTime() > now.getTime();
    // The first planned appearance: the waiting copy's day, else the next planned day after today.
    let day = waiting ? startOfDay(new Date(t.available_from as any)) : nextPlannedDay(plan, now, false);
    let n = 0;
    while (day && day < until && n < 40) {
      const within = Number(t.days_to_complete) > 0 ? Number(t.days_to_complete) : 0;
      const at = new Date(day.getFullYear(), day.getMonth(), day.getDate() + within, within ? 20 : 0);
      (out[dayKey(at)] ||= []).push({ ...t, id: `planned-${t.series_id || t.id}-${dayKey(day)}`, assigned_to: who, due_date: at.toISOString(), planned: true });
      day = nextPlannedDay(plan, day, false);
      n++;
    }
  }
  return out;
}
